import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

/** Admin → Pricing → Donations. Controls the "Help Fuel Startup Otter" block on /pricing. */
export default function DonationSettingsEditor() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/donations/settings").then(({ data }) => setS(data)).catch(() => {});
  }, []);

  if (!s) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const amounts = String(s.suggested_amounts)
        .split(",").map((x) => parseFloat(String(x).trim())).filter((x) => x > 0);
      const { data } = await api.put("/admin/donations/settings", {
        enabled: !!s.enabled,
        title: s.title, blurb: s.blurb, button_label: s.button_label,
        suggested_amounts: amounts,
        min_amount: parseFloat(s.min_amount) || 1,
        max_amount: parseFloat(s.max_amount) || 10000,
        allow_monthly: !!s.allow_monthly,
        note: s.note,
      });
      setS(data);
      toast.success("Donations settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5" data-testid="donation-settings-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-[680] text-foreground">Donations block</h3>
          <p className="text-xs text-muted-foreground">Shown on the public pricing page under the Founders cards.</p>
        </div>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Switch checked={!!s.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="donation-enabled-toggle" />
            <Label className="text-sm">Visible</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={!!s.allow_monthly} onCheckedChange={(v) => set("allow_monthly", v)} data-testid="donation-monthly-toggle" />
            <Label className="text-sm">Allow monthly</Label>
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Headline</Label>
          <Input value={s.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="donation-title-input" />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Blurb</Label>
          <Textarea rows={3} value={s.blurb || ""} onChange={(e) => set("blurb", e.target.value)} data-testid="donation-blurb-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Button label</Label>
          <Input value={s.button_label || ""} onChange={(e) => set("button_label", e.target.value)} data-testid="donation-button-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Suggested amounts <span className="text-muted-foreground">(comma separated)</span></Label>
          <Input
            value={Array.isArray(s.suggested_amounts) ? s.suggested_amounts.join(", ") : s.suggested_amounts}
            onChange={(e) => set("suggested_amounts", e.target.value)}
            data-testid="donation-amounts-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Minimum ($)</Label>
          <Input type="number" min="1" step="1" value={s.min_amount} onChange={(e) => set("min_amount", e.target.value)} data-testid="donation-min-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Maximum ($)</Label>
          <Input type="number" min="1" step="1" value={s.max_amount} onChange={(e) => set("max_amount", e.target.value)} data-testid="donation-max-input" />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Small print</Label>
          <Input value={s.note || ""} onChange={(e) => set("note", e.target.value)} data-testid="donation-note-input" />
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="donation-save-button">
          <Save className="mr-1.5 h-4 w-4" /> {saving ? "Saving…" : "Save donations settings"}
        </Button>
      </div>
    </Card>
  );
}
